import { motion } from 'framer-motion'
import { Link } from '@tanstack/react-router'
import { Users, CreditCard, CalendarDays, BarChart3, ArrowRight } from 'lucide-react'
import { slideUp, slideUpStagger, slideUpStaggerItem, withReducedMotion } from '@/lib/animations'

/**
 * Studio features section for landing page
 * Highlights studio management capabilities
 */
export function StudioFeaturesSection() {
  const studioFeatures = [
    {
      icon: Users,
      title: 'Manage Members',
      description: 'Track member status, add private notes, and manage your community with ease.',
    },
    {
      icon: CreditCard,
      title: 'Billing Made Easy',
      description: 'Offer flexible membership plans, class passes, and drop-in options.',
    },
    {
      icon: CalendarDays,
      title: 'Schedule Classes',
      description: 'Create recurring classes, manage capacity, and track attendance effortlessly.',
    },
    {
      icon: BarChart3,
      title: 'Insights & Reporting',
      description: 'Understand your studio performance with detailed analytics and reports.',
    },
  ]

  return (
    <section id="for-studios" className="scroll-mt-20 bg-gradient-to-b from-slate-50 to-white py-24 dark:from-slate-900 dark:to-slate-950 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="mb-16 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={withReducedMotion(slideUp)}
        >
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            Built for{' '}
            <span className="text-purple-400">Studios & Gyms</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
            Everything you need to run your aerial studio smoothly
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          className="grid grid-cols-1 gap-8 md:grid-cols-2"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={withReducedMotion(slideUpStagger)}
        >
          {studioFeatures.map((feature) => (
            <motion.div
              key={feature.title}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-8 transition-all hover:border-purple-400/50 hover:shadow-lg dark:border-slate-800 dark:bg-slate-900 dark:hover:border-purple-400/50"
              variants={withReducedMotion(slideUpStaggerItem)}
            >
              {/* Background Gradient on Hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 to-transparent opacity-0 transition-opacity group-hover:opacity-100 dark:from-purple-950/20" />

              {/* Content */}
              <div className="relative">
                <div className="mb-4 inline-flex rounded-xl bg-purple-50 p-3 dark:bg-purple-950/30">
                  <feature.icon className="h-8 w-8 text-purple-400" />
                </div>

                <h3 className="mb-3 text-xl font-bold text-slate-900 dark:text-white">
                  {feature.title}
                </h3>

                <p className="text-slate-600 dark:text-slate-400">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          className="mt-16 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={withReducedMotion(slideUp)}
        >
          <Link
            to="/"
            className="group inline-flex items-center gap-2 rounded-lg bg-purple-400 px-8 py-4 text-lg font-semibold text-white transition-all hover:bg-purple-500 hover:shadow-lg hover:shadow-purple-400/50"
          >
            Get Started for Your Studio
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
